import { calculateRms } from "@jugl-web/utils/audio/calculateRms";

/** Number of samples for the analyser (affects accuracy). */
const FFT_SIZE = 256;

/** If RMS goes above this, we start counting as talking. */
const SPEAKING_THRESHOLD = 0.03;

/** If RMS drops below this, we start counting as silent. */
const SILENCE_THRESHOLD = 0.015;

/** Time RMS has to be above the speak level to turn on `isSpeaking`. */
const ACTIVATION_MS = 180;

/** Time RMS needs to stay low before setting `isSpeaking` to false. */
const HANGOVER_MS = 350;

export interface VoiceModeUserInputAnalysisOptions {
  /** Fired when the boolean speaking state changes. */
  onSpeakingChange?: (isSpeaking: boolean) => void;
}

/**
 * Class for processing microphone input to detect when the user is speaking,
 * and providing waveform data for the UI.
 */
export class VoiceModeUserInputAnalysis {
  private readonly samples: Float32Array<ArrayBuffer>;
  private readonly onSpeakingChange: (isSpeaking: boolean) => void;

  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;

  private rafId: number | null = null;
  private isRunning = false;
  private isDisposed = false;

  private isSpeaking = false;
  /** When did the current loud or quiet period start. */
  private energyStreakStartedAt: number | null = null;

  constructor({ onSpeakingChange }: VoiceModeUserInputAnalysisOptions = {}) {
    this.samples = new Float32Array(
      new ArrayBuffer(FFT_SIZE * Float32Array.BYTES_PER_ELEMENT)
    );
    this.onSpeakingChange = onSpeakingChange ?? noop;
  }

  /**
   * Returns the live time-domain sample buffer (mutated each animation frame).
   */
  public getWaveformSamples(): Float32Array<ArrayBuffer> {
    return this.samples;
  }

  /**
   * Requests microphone access, builds the analyser graph, and starts the loop.
   * No-ops when already running.
   */
  public async start(): Promise<void> {
    if (this.isDisposed) {
      throw new Error("VoiceModeUserInputAnalysis has been disposed");
    }

    if (this.isRunning) {
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    // Stop if this was disposed while waiting for mic access.
    if (this.isDisposed) {
      stream.getAudioTracks().forEach((track) => {
        track.stop();
      });
      return;
    }

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = FFT_SIZE;
    source.connect(analyser);

    this.mediaStream = stream;
    this.audioContext = audioContext;
    this.source = source;
    this.analyser = analyser;

    if (audioContext.state === "suspended") {
      audioContext.resume();
    }

    this.isRunning = true;
    this.tick();
  }

  /**
   * Stops the analysis loop, releases the microphone, and clears samples/VAD.
   */
  public stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.isRunning = false;
    this.samples.fill(0);
    this.energyStreakStartedAt = null;
    this.setSpeaking(false);

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getAudioTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = null;
    }
  }

  /**
   * Stops capture/analysis and permanently releases all resources.
   */
  public dispose(): void {
    if (this.isDisposed) {
      return;
    }

    this.stop();
    this.isDisposed = true;
  }

  /**
   * Pulls the latest time-domain data, updates VAD state, and schedules the next frame.
   */
  private tick = (): void => {
    if (!this.isRunning || this.isDisposed || !this.analyser) {
      return;
    }

    this.analyser.getFloatTimeDomainData(this.samples);
    this.updateSpeakingState(performance.now(), calculateRms(this.samples));

    this.rafId = requestAnimationFrame(this.tick);
  };

  /**
   * Updates the speaking state based on the current audio level and specified thresholds.
   */
  private updateSpeakingState(now: number, level: number): void {
    if (!this.isSpeaking) {
      if (level >= SPEAKING_THRESHOLD) {
        if (this.energyStreakStartedAt === null) {
          this.energyStreakStartedAt = now;
        }

        if (now - this.energyStreakStartedAt >= ACTIVATION_MS) {
          this.energyStreakStartedAt = null;
          this.setSpeaking(true);
        }
      } else {
        this.energyStreakStartedAt = null;
      }

      return;
    }

    if (level <= SILENCE_THRESHOLD) {
      if (this.energyStreakStartedAt === null) {
        this.energyStreakStartedAt = now;
      }

      if (now - this.energyStreakStartedAt >= HANGOVER_MS) {
        this.energyStreakStartedAt = null;
        this.setSpeaking(false);
      }
    } else {
      this.energyStreakStartedAt = null;
    }
  }

  /**
   * Updates speaking state and notifies listeners only when the value changes.
   */
  private setSpeaking(isSpeaking: boolean): void {
    if (this.isSpeaking === isSpeaking) {
      return;
    }

    this.isSpeaking = isSpeaking;
    this.onSpeakingChange(isSpeaking);
  }
}

const noop = () => {};
